// src/components/layout/Layout.jsx - FIXED VERSION

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import MobileNav from './MobileNav';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile]       = useState(false);
  const [isTablet, setIsTablet]       = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking]   = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ location ka ref rakho taaki stale closure na ho
  const locationRef = useRef(location.pathname);
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  /* ══════════════════════════════════════════════
     ✅ FIXED AUTH CHECK
  ══════════════════════════════════════════════ */
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const role  = localStorage.getItem('role');

      // ✅ FIX 1: setIsChecking(false) har case mein
      if (!token) {
        setIsChecking(false); // ← ADDED
        navigate('/login', {
          replace: true,
          state: { from: locationRef.current },
        });
        return;
      }

      if (role && role !== 'user') {
        const redirectMap = {
          admin:      '/Admin_Panel',
          superadmin: '/Admin_Panel',
          developer:  '/Admin_Panel',
          staff:      '/Staff_Panel',
        };
        setIsChecking(false); // ← ADDED
        navigate(redirectMap[role] || '/login', { replace: true });
        return;
      }

      // ✅ Normal authenticated user
      setIsAuthenticated(true);
      setIsChecking(false);
    };

    checkAuth();
  }, []); // ✅ locationRef use kiya toh dependency nahi chahiye

  /* ══════════════════════════════════════════════
     RESPONSIVE DETECTION
  ══════════════════════════════════════════════ */
  const handleResize = useCallback(() => {
    const w      = window.innerWidth;
    const mobile = w < 768;
    const tablet = w >= 768 && w < 1024;

    setIsMobile(mobile);
    setIsTablet(tablet);

    if (mobile) {
      setSidebarOpen(false);
    } else if (tablet) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Close sidebar on route change
  useEffect(() => {
    if (isMobile || isTablet) setSidebarOpen(false);
  }, [location.pathname, isMobile, isTablet]);

  const toggleSidebar = () => setSidebarOpen(v => !v);

  /* ══════════════════════════════════════════════
     ✅ FIXED SIDEBAR WIDTH — CSS variable use karo
     JS state → CSS custom property → no FOUC
  ══════════════════════════════════════════════ */
  const getSidebarWidth = () => {
    if (isMobile)    return 0;
    if (sidebarOpen) return 260;
    return isTablet ? 0 : 80;
  };

  /* ══════════════════════════════════════════════
     LOADING SCREEN
  ══════════════════════════════════════════════ */
  if (isChecking) {
    return (
      <>
        <style>{`
          @keyframes lspin {
            to { transform: rotate(360deg); }
          }
          @keyframes lpulse {
            0%,100% { opacity: 1; transform: scale(1);    }
            50%     { opacity: .6; transform: scale(1.1); }
          }
          .ls-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100dvh;
            background: linear-gradient(135deg, #fff5f7, #fff);
            gap: 18px;
            font-family: 'Poppins', sans-serif;
            padding: 24px;
          }
          .ls-logo  { font-size: 40px; animation: lpulse 1.8s ease-in-out infinite; }
          .ls-brand {
            font-size: 22px; font-weight: 800; margin: 0;
            background: linear-gradient(135deg, #ff0055, #ff5c93);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .ls-ring {
            width: 52px; height: 52px;
            border: 4px solid #ffe4ec;
            border-top-color: #ff0055;
            border-radius: 50%;
            animation: lspin 1s linear infinite;
          }
          .ls-text { color: #aaa; font-size: 13px; margin: 0; font-weight: 500; }
        `}</style>
        <div className="ls-wrap">
          <p className="ls-logo">💖</p>
          <p className="ls-brand">MatrimonySite</p>
          <div className="ls-ring" />
          <p className="ls-text">Verifying your session…</p>
        </div>
      </>
    );
  }

  if (!isAuthenticated) return null;

  /* ══════════════════════════════════════════════
     MAIN LAYOUT
  ══════════════════════════════════════════════ */
  const sidebarWidth = getSidebarWidth();

  return (
    <>
      <style>{`
        /* ── Global reset ───────────────────────── */
        *, *::before, *::after { box-sizing: border-box; }

        /* ── Root container ─────────────────────── */
        .ly-root {
          display: flex;
          height: 100dvh;
          background: #f4f6f9;
          overflow: hidden;
          position: relative;
        }

        /* ── Sidebar slot ───────────────────────── */
        /* ✅ FIX 3: width inline style se — no CSS injection */
        .ly-sidebar-slot {
          flex-shrink: 0;
          transition: width 0.3s ease;
          overflow: visible;
          position: relative;
          z-index: 50;
        }

        /* ── Main column ────────────────────────── */
        .ly-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        /* ── Scrollable page area ───────────────── */
        .ly-content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-y: contain;
          padding: 24px;
          background: #f4f6f9;
          scroll-behavior: smooth;
        }
        .ly-content::-webkit-scrollbar       { width: 4px; }
        .ly-content::-webkit-scrollbar-thumb {
          background: #ffc2d1;
          border-radius: 999px;
        }
        .ly-content::-webkit-scrollbar-track { background: transparent; }

        .ly-content-inner {
          max-width: 1280px;
          margin: 0 auto;
          width: 100%;
        }

        /* ── Mobile bottom-nav spacer ───────────── */
        .ly-mobile-pad { height: 72px; flex-shrink: 0; }

        /* ── Sidebar overlay ────────────────────── */
        .ly-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.5);
          z-index: 45;
          animation: ovFade .2s ease;
          cursor: pointer;
        }
        @keyframes ovFade { from { opacity: 0; } to { opacity: 1; } }
        .ly-overlay.active { display: block; }

        /* ══════════════════════════════════════
           RESPONSIVE BREAKPOINTS
        ══════════════════════════════════════ */

        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .ly-content { padding: 18px 16px; }
        }

        /* Mobile */
        @media (max-width: 767px) {
          .ly-content { padding: 14px 12px; }
        }

        /* Small mobile */
        @media (max-width: 479px) {
          .ly-content { padding: 12px 10px; }
        }

        /* Extra small */
        @media (max-width: 359px) {
          .ly-content { padding: 10px 8px; }
        }

        /* Landscape short */
        @media (max-height: 500px) and (orientation: landscape) {
          .ly-content      { padding: 8px 14px; }
          .ly-mobile-pad   { height: 56px; }
        }
      `}</style>

      <div className="ly-root">

        {/* ── SIDEBAR ── */}
        {/* ✅ FIX 3: width inline style — smooth transition without CSS injection */}
        <div
          className="ly-sidebar-slot"
          style={{ width: sidebarWidth }}
        >
          <Sidebar
            isOpen={sidebarOpen}
            setIsOpen={setSidebarOpen}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </div>

        {/* ── OVERLAY ── */}
        <div
          className={`ly-overlay${(isMobile || isTablet) && sidebarOpen ? ' active' : ''}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />

        {/* ── MAIN ── */}
        <div className="ly-main">

          <Header
            toggleSidebar={toggleSidebar}
            isMobile={isMobile}
            isTablet={isTablet}
            sidebarOpen={sidebarOpen}
          />

          <main className="ly-content" id="main-content">
            <div className="ly-content-inner">
              <Outlet />
            </div>

            {/* Bottom spacer for mobile nav */}
            {isMobile && <div className="ly-mobile-pad" aria-hidden="true" />}
          </main>

          {isMobile && <MobileNav />}
        </div>
      </div>
    </>
  );
};

export default Layout;